/**
 * The Ops cancel 7-day split (W5 m9, vault boards 02 + 09) — ONE pure rule the
 * web store mirror (apps/web opsCancelFlow) and the atomic server intent
 * (POST /api/intents/ops-cancel) both apply, so the client and the DB can never
 * split a deposit differently.
 *
 *   - The booking goes `cancelled` with its typed reason, who and when.
 *   - INSIDE 7 days a paid deposit is FORFEITED to the house: status
 *     'forfeited' + a `forfeit` ledger movement that nets the paid balance to 0
 *     (it stays linked to the booking). Reinstating sets it back to 'paid'.
 *   - OUTSIDE 7 days a paid deposit is KEPT ON FILE as deposit-no-date: unlinked
 *     from the booking with the 12-month on-file validity, still 'paid', so it
 *     drops into the Sweeper's pool.
 *   - An UNPAID link holding this slot is voided ('expired'), so it can't be paid
 *     for a dead slot.
 *   - Money in is split whether the deposit is 'paid' or still 'pending' (a
 *     top-up under the $50 secure line): any ledgerPaidAud > 0 counts.
 *   - The deposit's TOP-UP PLAN is cancelled and its unpaid instalments voided
 *     (DECIDED 24 Sep, Q2, tv-2na.8.16); the paid ones follow the split above.
 *   - A confirmation call behind the cancel stamps a 'cancelled' touch.
 *
 * "No date" on a reschedule (applyNoDateRelease) is NOT a cancel: the money is
 * always kept on file, even inside 7 days (DECIDED 24 Sep, Q6, tv-2na.11.13).
 *
 * "Inside" compares whole studio calendar days (Melbourne), matching the web's
 * differenceInCalendarDays(start, now) < 7 under TZ=Australia/Melbourne.
 */
import { studioDayKey } from './bookingStates';
import { slotBookingId } from './slotKey';

export const CANCEL_WINDOW_DAYS = 7;

/** Pre-arrival (and checked-in) statuses an Ops cancel may act on. */
const CANCELLABLE: ReadonlySet<string> = new Set(['held', 'pending-deposit', 'confirmed', 'checked-in']);
/** Not yet arrived: a past one of these was never attended (Q5 — offer no-show, not cancel). */
const PRE_ARRIVAL: ReadonlySet<string> = new Set(['held', 'pending-deposit', 'confirmed']);

const DAY_MS = 86_400_000;

/** Whole studio calendar days from `atIso`'s day to `startIso`'s day. */
function calendarDaysBetween(atIso: string, startIso: string): number {
  const a = Date.parse(`${studioDayKey(atIso)}T00:00:00Z`);
  const b = Date.parse(`${studioDayKey(startIso)}T00:00:00Z`);
  return Math.round((b - a) / DAY_MS);
}

/**
 * The appointment's studio day is behind today (DECIDED 25 Sep, Q5): a past,
 * never-attended booking is offered "Mark no-show", never cancel. Earlier today
 * is not past (a same-day cancel still applies the 7-day split).
 */
export function isPastBooking(startIso: string, atIso: string): boolean {
  return studioDayKey(startIso) < studioDayKey(atIso);
}

/** A booking the cancel intent would accept right now: a cancellable status and not past. */
export function isCancellableNow(b: { status: string; start: string }, atIso: string): boolean {
  if (!CANCELLABLE.has(b.status)) return false;
  return !(PRE_ARRIVAL.has(b.status) && isPastBooking(b.start, atIso));
}

/** True when the appointment is fewer than 7 whole calendar days away. */
export function isInsideCancelWindow(startIso: string, atIso: string): boolean {
  return calendarDaysBetween(atIso, startIso) < CANCEL_WINDOW_DAYS;
}

/** 12 months from the instant a deposit goes on file (vault board 02: valid 12 months). */
export function onFileExpiryFrom(atIso: string): string {
  const d = new Date(atIso);
  d.setUTCMonth(d.getUTCMonth() + 12);
  return d.toISOString();
}

export interface CancelSplitLedgerEntry {
  at: string;
  amountAud: number;
  kind: string;
  by?: string;
  note?: string;
  fromBookingId?: string;
}

export interface CancelSplitTouch {
  at: string;
  channel: 'call';
  outcome: 'cancelled';
  by: string;
}

/** The booking fields the split reads/writes. The web `Booking` fits structurally. */
export interface CancelSplitBooking {
  id: string;
  status: string;
  start: string;
  depositId?: string;
  confirmationTouches?: ReadonlyArray<{ at: string; channel: string; outcome: string; by: string }>;
  cancelReason?: string;
  cancelledBy?: string;
  cancelledAt?: string;
}

/** The deposit fields the split reads/writes. The web `Deposit` fits structurally. */
export interface CancelSplitDeposit {
  id: string;
  status: string;
  amountAud: number;
  amountPaid?: number;
  paidAt?: string;
  bookingId?: string;
  heldBookingId?: string;
  expiresAt: string;
  takenByAgentId?: string;
  ledger?: ReadonlyArray<CancelSplitLedgerEntry>;
}

/** Money actually on the deposit: the ledger nets it when present (mirrors web depositPaidAud). */
export function ledgerPaidAud(d: Pick<CancelSplitDeposit, 'ledger' | 'amountPaid' | 'status' | 'amountAud'>): number {
  if (d.ledger && d.ledger.length > 0) {
    return d.ledger.reduce((sum, e) => {
      switch (e.kind) {
        case 'paid':
        case 'topup':
        case 'rollover-in':
        case 'reinstate':
          return sum + Number(e.amountAud);
        case 'rollover-out':
        case 'forfeit':
          return sum - Number(e.amountAud);
        default:
          return sum;
      }
    }, 0);
  }
  return d.amountPaid ?? (d.status === 'paid' ? d.amountAud : 0);
}

/** The top-up instalment fields the plan cancel reads/writes. The web `TopupInstalment` fits. */
export interface CancelSplitInstalment {
  status: string;
  dueIso?: string;
  amountAud?: number;
  paidAt?: string;
}

/** The top-up plan fields the plan cancel reads/writes. The web `TopupPlan` fits structurally. */
export interface CancelSplitPlan {
  id: string;
  status: string;
  depositId: string;
  bookingId?: string;
  instalments: ReadonlyArray<CancelSplitInstalment>;
}

const UNPAID_INSTALMENT: ReadonlySet<string> = new Set(['scheduled', 'reminded', 'missed']);

/**
 * Cancel a top-up plan (DECIDED 24 Sep, Q2): status 'cancelled' and every unpaid
 * instalment voided ('cancelled'), so nothing is chased or charged again. Paid
 * instalments stay paid — that money follows the deposit's split. Undefined = the
 * plan is not active (completed / already cancelled), so there is nothing to write.
 */
export function cancelTopupPlan<P extends CancelSplitPlan>(plan: P): P | undefined {
  if (plan.status !== 'active') return undefined;
  return {
    ...plan,
    status: 'cancelled',
    instalments: plan.instalments.map((i) => (UNPAID_INSTALMENT.has(i.status) ? { ...i, status: 'cancelled' } : i)),
  };
}

/** The plan belongs to this booking's deposit (or, dateless, to the booking itself). */
function planOf<P extends CancelSplitPlan>(
  plan: P | undefined,
  bookingId: string,
  deposit: CancelSplitDeposit | undefined,
): P | undefined {
  if (!plan) return undefined;
  return (deposit && plan.depositId === deposit.id) || plan.bookingId === bookingId ? plan : undefined;
}

/**
 * Keep a deposit ON FILE (deposit-no-date, vault board 02): unlinked from its
 * booking (both refs) with a fresh 12-month validity from `atIso`, so it drops
 * into the Sweeper's pool. A still-'pending' deposit with money in becomes the
 * PART-PAID state ('paid' with amountPaid below amountAud) — money on file is
 * paid money, never a live link.
 */
export function keepOnFile<D extends CancelSplitDeposit>(deposit: D, atIso: string): D {
  const paid = ledgerPaidAud(deposit);
  return {
    ...deposit,
    ...(deposit.status === 'pending'
      ? { status: 'paid', amountPaid: deposit.amountPaid ?? paid, paidAt: deposit.paidAt ?? atIso }
      : {}),
    bookingId: undefined,
    heldBookingId: undefined,
    expiresAt: onFileExpiryFrom(atIso),
  };
}

/** Money is on the deposit and it is still the booking's (paid or a pending top-up). */
function hasMoneyIn(deposit: CancelSplitDeposit): boolean {
  return (deposit.status === 'paid' || deposit.status === 'pending') && ledgerPaidAud(deposit) > 0;
}

export type CancelDepositOutcome = 'forfeited' | 'kept-on-file' | 'link-voided' | 'none';

export interface ApplyCancelSplitInput<
  B extends CancelSplitBooking,
  D extends CancelSplitDeposit,
  P extends CancelSplitPlan = CancelSplitPlan,
> {
  booking: B;
  /** The booking's deposit (booking.depositId), when it has one. */
  deposit?: D;
  /** The deposit's top-up plan, when it has one: cancelled with the booking (Q2). */
  plan?: P;
  reason: string;
  by: string;
  atIso: string;
  /** The confirmation call this cancel resolves, if any — stamps a 'cancelled' touch. */
  callId?: string;
}

export type ApplyCancelSplitResult<B, D, P = CancelSplitPlan> =
  | {
      ok: true;
      booking: B;
      /** The updated deposit, only when the split changed it. */
      deposit?: D;
      /** The cancelled top-up plan (unpaid instalments voided), only when one was active. */
      plan?: P;
      depositOutcome: CancelDepositOutcome;
      /** The forfeit movement appended to the deposit ledger (inside 7 days). */
      ledgerEntry?: CancelSplitLedgerEntry;
      /** The 'cancelled' touch appended to the booking (callId given). */
      touch?: CancelSplitTouch;
    }
  | { ok: false; error: 'missing-reason' | 'already-cancelled' | 'not-cancellable' | 'past-booking' };

export function applyCancelSplit<
  B extends CancelSplitBooking,
  D extends CancelSplitDeposit,
  P extends CancelSplitPlan = CancelSplitPlan,
>(input: ApplyCancelSplitInput<B, D, P>): ApplyCancelSplitResult<B, D, P> {
  const reason = input.reason.trim();
  if (!reason) return { ok: false, error: 'missing-reason' };
  const { booking, deposit, by, atIso } = input;
  if (booking.status === 'cancelled') return { ok: false, error: 'already-cancelled' };
  if (!CANCELLABLE.has(booking.status)) return { ok: false, error: 'not-cancellable' };
  if (PRE_ARRIVAL.has(booking.status) && isPastBooking(booking.start, atIso)) return { ok: false, error: 'past-booking' };

  const touch: CancelSplitTouch | undefined = input.callId
    ? { at: atIso, channel: 'call', outcome: 'cancelled', by }
    : undefined;
  const nextBooking: B = {
    ...booking,
    status: 'cancelled',
    cancelReason: reason,
    cancelledBy: by,
    cancelledAt: atIso,
    ...(touch ? { confirmationTouches: [...(booking.confirmationTouches ?? []), touch] } : {}),
  };
  const cancelledPlan = (() => {
    const p = planOf(input.plan, booking.id, deposit);
    return p ? cancelTopupPlan(p) : undefined;
  })();
  const extras = { ...(touch ? { touch } : {}), ...(cancelledPlan ? { plan: cancelledPlan } : {}) };

  const linked =
    deposit && (booking.depositId === deposit.id || deposit.bookingId === booking.id || deposit.heldBookingId === booking.id);
  if (!deposit || !linked) return { ok: true, booking: nextBooking, depositOutcome: 'none', ...extras };

  if (hasMoneyIn(deposit)) {
    const paid = ledgerPaidAud(deposit);
    if (isInsideCancelWindow(booking.start, atIso)) {
      const baseLedger: CancelSplitLedgerEntry[] =
        deposit.ledger && deposit.ledger.length > 0
          ? [...deposit.ledger]
          : [{ at: deposit.paidAt ?? atIso, amountAud: paid, kind: 'paid', by: deposit.takenByAgentId ?? 'system' }];
      const ledgerEntry: CancelSplitLedgerEntry = {
        at: atIso,
        amountAud: paid,
        kind: 'forfeit',
        fromBookingId: booking.id,
        by,
        note: `Ops cancel inside 7 days: ${reason}`,
      };
      return {
        ok: true,
        booking: nextBooking,
        deposit: { ...deposit, status: 'forfeited', ledger: [...baseLedger, ledgerEntry] },
        depositOutcome: 'forfeited',
        ledgerEntry,
        ...extras,
      };
    }
    return { ok: true, booking: nextBooking, deposit: keepOnFile(deposit, atIso), depositOutcome: 'kept-on-file', ...extras };
  }

  if (deposit.status === 'pending' && slotBookingId(deposit) === booking.id) {
    return {
      ok: true,
      booking: nextBooking,
      deposit: { ...deposit, status: 'expired' },
      depositOutcome: 'link-voided',
      ...extras,
    };
  }
  return { ok: true, booking: nextBooking, depositOutcome: 'none', ...extras };
}

export interface ApplyNoDateReleaseInput<
  B extends CancelSplitBooking,
  D extends CancelSplitDeposit,
  P extends CancelSplitPlan = CancelSplitPlan,
> {
  /** The booking being released (it goes superseded; the caller writes that + its history). */
  booking: B;
  deposit?: D;
  plan?: P;
  atIso: string;
}

export interface ApplyNoDateReleaseResult<D, P = CancelSplitPlan> {
  /** The updated deposit, only when the release changed it. */
  deposit?: D;
  /** The cancelled top-up plan, only when one was active. */
  plan?: P;
  depositOutcome: Exclude<CancelDepositOutcome, 'forfeited'>;
}

/**
 * "No date" on a reschedule (vault board 06, tv-2na.11.13): the slot is released
 * and the money stays ON FILE for 12 months — ALWAYS, even inside 7 days, because
 * it is a reschedule, not a cancel (DECIDED 24 Sep, Q6). The Sweeper chases it.
 * The top-up plan is cancelled with its unpaid instalments voided (as a cancel,
 * Q2); an unpaid link held on the released slot is voided.
 */
export function applyNoDateRelease<
  B extends CancelSplitBooking,
  D extends CancelSplitDeposit,
  P extends CancelSplitPlan = CancelSplitPlan,
>(input: ApplyNoDateReleaseInput<B, D, P>): ApplyNoDateReleaseResult<D, P> {
  const { booking, deposit, atIso } = input;
  const p = planOf(input.plan, booking.id, deposit);
  const cancelledPlan = p ? cancelTopupPlan(p) : undefined;
  const extras = cancelledPlan ? { plan: cancelledPlan } : {};

  const linked = deposit && (deposit.bookingId === booking.id || deposit.heldBookingId === booking.id);
  if (!deposit || !linked) return { depositOutcome: 'none', ...extras };
  if (hasMoneyIn(deposit)) return { deposit: keepOnFile(deposit, atIso), depositOutcome: 'kept-on-file', ...extras };
  if (deposit.status === 'pending' && slotBookingId(deposit) === booking.id) {
    return { deposit: { ...deposit, status: 'expired' }, depositOutcome: 'link-voided', ...extras };
  }
  return { depositOutcome: 'none', ...extras };
}

// ---------------------------------------------------------------------------
// W5b #1 (tv-2na.12.17): the ONE cancel intent also marks no-shows (Q6) and
// cancels a dateless top-up plan; the modal reads honest amounts from
// cancelConsequence (D23/D24).
// ---------------------------------------------------------------------------

/** The paid history a forfeit nets against (backfills one 'paid' row for a ledger-less deposit). */
function forfeitLedger<D extends CancelSplitDeposit>(deposit: D, paid: number, atIso: string): CancelSplitLedgerEntry[] {
  return deposit.ledger && deposit.ledger.length > 0
    ? [...deposit.ledger]
    : [{ at: deposit.paidAt ?? atIso, amountAud: paid, kind: 'paid', by: deposit.takenByAgentId ?? 'system' }];
}

export interface ApplyNoShowInput<
  B extends CancelSplitBooking,
  D extends CancelSplitDeposit,
  P extends CancelSplitPlan = CancelSplitPlan,
> {
  booking: B;
  deposit?: D;
  plan?: P;
  reason: string;
  by: string;
  atIso: string;
}

export type ApplyNoShowResult<B, D, P = CancelSplitPlan> =
  | {
      ok: true;
      booking: B;
      /** The deposit with the forfeit movement appended, only when money was in. */
      deposit?: D;
      plan?: P;
      depositOutcome: 'forfeited' | 'none';
      forfeitedAud: number;
      ledgerEntry?: CancelSplitLedgerEntry;
    }
  | { ok: false; error: 'missing-reason' | 'already-no-show' | 'not-no-showable' | 'not-yet-due' };

/**
 * Mark a booking no-show (DECIDED 25 Sep, Q5 + Q6). Only a pre-arrival booking
 * whose appointment has started can be a no-show. Any money in is forfeited to
 * the house with a `forfeit` movement carrying the typed reason; the deposit
 * stays linked (and 'paid', so it reads as lapsed — reinstatable). The top-up
 * plan is cancelled with its unpaid instalments voided.
 */
export function applyNoShow<
  B extends CancelSplitBooking,
  D extends CancelSplitDeposit,
  P extends CancelSplitPlan = CancelSplitPlan,
>(input: ApplyNoShowInput<B, D, P>): ApplyNoShowResult<B, D, P> {
  const reason = input.reason.trim();
  if (!reason) return { ok: false, error: 'missing-reason' };
  const { booking, deposit, by, atIso } = input;
  if (booking.status === 'no-show') return { ok: false, error: 'already-no-show' };
  if (!PRE_ARRIVAL.has(booking.status)) return { ok: false, error: 'not-no-showable' };
  if (Date.parse(booking.start) > Date.parse(atIso)) return { ok: false, error: 'not-yet-due' };

  const nextBooking: B = { ...booking, status: 'no-show' };
  const p = planOf(input.plan, booking.id, deposit);
  const cancelledPlan = p ? cancelTopupPlan(p) : undefined;
  const extras = cancelledPlan ? { plan: cancelledPlan } : {};
  const linked =
    deposit && (booking.depositId === deposit.id || deposit.bookingId === booking.id || deposit.heldBookingId === booking.id);
  if (!deposit || !linked || !hasMoneyIn(deposit)) {
    return { ok: true, booking: nextBooking, depositOutcome: 'none', forfeitedAud: 0, ...extras };
  }
  const paid = ledgerPaidAud(deposit);
  const ledgerEntry: CancelSplitLedgerEntry = { at: atIso, amountAud: paid, kind: 'forfeit', fromBookingId: booking.id, by, note: reason };
  return {
    ok: true,
    booking: nextBooking,
    deposit: { ...deposit, ledger: [...forfeitLedger(deposit, paid, atIso), ledgerEntry] },
    depositOutcome: 'forfeited',
    forfeitedAud: paid,
    ledgerEntry,
    ...extras,
  };
}

export interface ApplyPlanCancelResult<D, P = CancelSplitPlan> {
  plan?: P;
  deposit?: D;
  depositOutcome: Exclude<CancelDepositOutcome, 'forfeited'>;
  keptOnFileAud: number;
}

/**
 * Cancel a DATELESS top-up plan (no booking to cancel): the plan is cancelled
 * with its unpaid instalments voided; the paid part stays on file for 12 months
 * (the Sweeper's pool); a plan with nothing paid voids its pending link.
 */
export function applyPlanCancel<D extends CancelSplitDeposit, P extends CancelSplitPlan = CancelSplitPlan>(input: {
  plan: P;
  deposit?: D;
  atIso: string;
}): ApplyPlanCancelResult<D, P> {
  const cancelled = cancelTopupPlan(input.plan);
  if (!cancelled) return { depositOutcome: 'none', keptOnFileAud: 0 };
  const { deposit, atIso } = input;
  if (deposit && hasMoneyIn(deposit)) {
    return { plan: cancelled, deposit: keepOnFile(deposit, atIso), depositOutcome: 'kept-on-file', keptOnFileAud: ledgerPaidAud(deposit) };
  }
  if (deposit && deposit.status === 'pending') {
    return { plan: cancelled, deposit: { ...deposit, status: 'expired' }, depositOutcome: 'link-voided', keptOnFileAud: 0 };
  }
  return { plan: cancelled, depositOutcome: 'none', keptOnFileAud: 0 };
}

export interface CancelConsequence {
  kind: 'forfeit' | 'on-file' | 'link-voided' | 'none' | 'past' | 'not-cancellable';
  /** The money that forfeits / goes on file: what was actually PAID, never the face value. */
  amountAud: number;
  daysOut: number;
  /** An active top-up plan is cancelled with the booking. */
  cancelsPlan: boolean;
  /** One plain sentence for the modal. */
  text: string;
}

const aud0 = (n: number) => `$${Math.round(n).toLocaleString('en-AU')}`;

/**
 * What a cancel WOULD do, in honest amounts (D23: inside 7 days forfeits, never
 * refunds; D24: a part-paid plan puts only the paid part on file). Pure — the
 * same applyCancelSplit rule, run as a preview.
 */
export function cancelConsequence<B extends CancelSplitBooking, D extends CancelSplitDeposit, P extends CancelSplitPlan>(input: {
  booking: B;
  deposit?: D;
  plan?: P;
  atIso: string;
}): CancelConsequence {
  const daysOut = calendarDaysBetween(input.atIso, input.booking.start);
  const res = applyCancelSplit({ ...input, reason: 'preview', by: 'preview' });
  const base = { daysOut, cancelsPlan: false, amountAud: 0 };
  if (!res.ok) {
    return res.error === 'past-booking'
      ? { ...base, kind: 'past', text: 'This appointment has passed. Mark it a no-show instead.' }
      : { ...base, kind: 'not-cancellable', text: "This booking can't be cancelled." };
  }
  const cancelsPlan = res.plan !== undefined;
  const planLine = cancelsPlan ? ' The top-up plan is cancelled, nothing more is charged.' : '';
  const paid = input.deposit ? ledgerPaidAud(input.deposit) : 0;
  const days = `${daysOut} day${daysOut === 1 ? '' : 's'} out`;
  switch (res.depositOutcome) {
    case 'forfeited':
      return { daysOut, cancelsPlan, amountAud: paid, kind: 'forfeit', text: `${days}, inside 7 days: the ${aud0(paid)} paid is forfeited to the house.${planLine}` };
    case 'kept-on-file':
      return { daysOut, cancelsPlan, amountAud: paid, kind: 'on-file', text: `${days}: the ${aud0(paid)} paid stays on file for 12 months.${planLine}` };
    case 'link-voided':
      return { daysOut, cancelsPlan, amountAud: 0, kind: 'link-voided', text: `${days}: nothing was paid, so the deposit link is voided.${planLine}` };
    default:
      return { daysOut, cancelsPlan, amountAud: 0, kind: 'none', text: `${days}: no deposit on file.${planLine}` };
  }
}
